import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Briefcase,
  CheckCircle2,
  Clock,
  Landmark,
  Loader2,
  MapPin,
  Search,
  UserRound,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PjCadastroPublicLayout } from '@/components/PjAgrada/PjCadastroPublicLayout'
import {
  PjCadastroFormSection,
  PjCadastroPanel,
  PjCadastroSelect,
} from '@/components/PjAgrada/PjCadastroFormSections'
import { EMPTY_PJ_AGRADA_FORM, type PjAgradaFormData } from '@/components/PjAgrada/pjAgradaTypes'
import {
  onlyDigits,
  maskCep,
  maskCnpj,
  maskCpf,
  maskPhone,
  validateCnpj,
} from '@/components/PjAgrada/pjAgradaFormat'
import { pjAgradaService } from '@/services/pjAgradaService'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { applyCepToPjForm, applyCnpjToPjForm, lookupPjCep, lookupPjCnpjPublic } from '@/utils/pjAgradaLookups'
import { PjCadastroCompletenessChecklist } from '@/components/PjAgrada/PjCadastroCompletenessChecklist'

function CenteredState({ children }: { children: React.ReactNode }) {
  return (
    <PjCadastroPublicLayout>
      <PjCadastroPanel>
        <div className='flex flex-col items-center justify-center py-10 text-center'>{children}</div>
      </PjCadastroPanel>
    </PjCadastroPublicLayout>
  )
}

export default function PjAgradaCadastroPage() {
  const { token = '' } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [emailLocked, setEmailLocked] = useState(false)
  const [emailHint, setEmailHint] = useState('')
  const [honeypot, setHoneypot] = useState('')
  const [cepLoading, setCepLoading] = useState(false)
  const [cnpjLoading, setCnpjLoading] = useState(false)
  const [form, setForm] = useState<PjAgradaFormData>(EMPTY_PJ_AGRADA_FORM)

  useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'referrer'
    meta.content = 'no-referrer'
    document.head.appendChild(meta)
    return () => {
      document.head.removeChild(meta)
    }
  }, [])

  useEffect(() => {
    if (!token || !/^[a-f0-9]{64}$/i.test(token)) {
      setError('Link inválido')
      setLoading(false)
      return
    }
    pjAgradaService
      .getCadastroInvite(token)
      .then((data) => {
        setExpiresAt(data.expiresAt)
        setEmailLocked(Boolean(data.emailLocked))
        setEmailHint(data.emailHint ?? '')
        setForm((prev) => ({
          ...prev,
          nomeCompleto: data.nomeCompleto ?? '',
          email: data.emailLocked && data.email ? data.email : prev.email,
          areaAtuacao: data.areaAtuacao ?? '',
        }))
      })
      .catch((err: unknown) => {
        setError(getFriendlyError(err, 'Link inválido ou expirado'))
      })
      .finally(() => setLoading(false))
  }, [token])

  const handleCepBlur = async () => {
    const digits = onlyDigits(form.cep)
    if (digits.length !== 8) return
    setCepLoading(true)
    try {
      const data = await lookupPjCep(digits)
      setForm((prev) => applyCepToPjForm(prev, data))
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'CEP não encontrado'))
    } finally {
      setCepLoading(false)
    }
  }

  const handleCnpjLookup = async () => {
    const digits = onlyDigits(form.cnpj)
    if (digits.length !== 14) {
      toast.error('CNPJ inválido — informe 14 dígitos')
      return
    }
    if (!validateCnpj(digits)) {
      toast.error('CNPJ inválido — verifique os dígitos informados')
      return
    }
    setCnpjLoading(true)
    try {
      const data = await lookupPjCnpjPublic(digits)
      setForm((prev) => applyCnpjToPjForm(prev, data))
      toast.success('Razão social e endereço preenchidos a partir do CNPJ')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'CNPJ não encontrado'))
    } finally {
      setCnpjLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!onlyDigits(form.cpf) && !onlyDigits(form.cnpj)) {
      toast.error('Informe CPF ou CNPJ')
      return
    }
    if (!form.bancoTipoConta) {
      toast.error('Selecione o tipo de conta')
      return
    }
    setSaving(true)
    try {
      await pjAgradaService.completeCadastro(token, form, honeypot)
      setSubmitted(true)
      toast.success('Cadastro enviado com sucesso')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao enviar cadastro'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <CenteredState>
        <Loader2 className='animate-spin text-emerald-600' size={36} />
        <p className='mt-4 text-sm text-[#6b778c]'>Carregando formulário…</p>
      </CenteredState>
    )
  }

  if (error) {
    return (
      <CenteredState>
        <p className='text-lg font-semibold text-[#172b4d]'>{error}</p>
        <p className='mt-2 max-w-sm text-sm text-[#6b778c]'>
          Solicite um novo link ao time financeiro da Agrada.
        </p>
      </CenteredState>
    )
  }

  if (submitted) {
    return (
      <CenteredState>
        <CheckCircle2 className='text-emerald-500' size={52} />
        <h1 className='mt-4 text-xl font-semibold text-[#172b4d]'>Cadastro recebido</h1>
        <p className='mt-2 max-w-sm text-sm leading-relaxed text-[#6b778c]'>
          Obrigado! Seus dados foram enviados ao financeiro Agrada. Você pode fechar esta página.
        </p>
      </CenteredState>
    )
  }

  return (
    <PjCadastroPublicLayout>
      <PjCadastroPanel>
        <header className='mb-6 space-y-3'>
          <div className='flex items-center gap-2 text-emerald-700'>
            <Briefcase size={22} aria-hidden />
            <h1 className='text-xl font-semibold text-[#172b4d] sm:text-2xl'>Cadastro</h1>
          </div>
          <p className='text-sm leading-relaxed text-[#6b778c]'>
            Preencha seus dados pessoais, endereço e informações bancárias para o financeiro Agrada.
            Campos marcados com <span className='font-medium text-[#172b4d]'>*</span> são obrigatórios.
          </p>
          {expiresAt && (
            <div className='inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800'>
              <Clock size={14} aria-hidden />
              Link válido até {new Date(expiresAt).toLocaleString('pt-BR')}
            </div>
          )}
        </header>

        <form onSubmit={(e) => void handleSubmit(e)} className='space-y-5'>
          <input
            type='text'
            name='website'
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            tabIndex={-1}
            autoComplete='off'
            aria-hidden='true'
            className='absolute left-[-9999px] h-0 w-0 opacity-0'
          />

          <PjCadastroFormSection
            icon={UserRound}
            title='Identificação'
            description='Informe CPF ou CNPJ (pelo menos um dos dois).'
          >
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
              <Input
                label='Nome completo *'
                required
                value={form.nomeCompleto}
                onChange={(e) => setForm({ ...form, nomeCompleto: e.target.value })}
              />
              <Input
                label='E-mail *'
                type='email'
                required
                readOnly={emailLocked}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={emailLocked && emailHint ? emailHint : undefined}
                hint={
                  emailLocked
                    ? 'E-mail definido no convite e preenchido automaticamente.'
                    : undefined
                }
              />
              <Input
                label='Telefone *'
                required
                value={form.telefone}
                onChange={(e) => setForm({ ...form, telefone: maskPhone(e.target.value) })}
                placeholder='(00) 00000-0000'
                inputMode='tel'
              />
              <Input
                label='CPF *'
                value={form.cpf}
                onChange={(e) => setForm({ ...form, cpf: maskCpf(e.target.value) })}
                placeholder='000.000.000-00'
                inputMode='numeric'
                maxLength={14}
                hint='Ou preencha o CNPJ abaixo.'
              />
              <div className='sm:col-span-2'>
                <div className='flex items-end gap-2'>
                  <div className='min-w-0 flex-1'>
                    <Input
                      label='CNPJ'
                      value={form.cnpj}
                      onChange={(e) => setForm({ ...form, cnpj: maskCnpj(e.target.value) })}
                      placeholder='00.000.000/0000-00'
                      inputMode='numeric'
                      maxLength={18}
                    />
                  </div>
                  <Button
                    type='button'
                    variant='secondary'
                    size='sm'
                    disabled={cnpjLoading}
                    className='mb-0 h-[38px] min-h-0 shrink-0 px-3 py-0'
                    onClick={() => void handleCnpjLookup()}
                    icon={cnpjLoading ? <Loader2 size={14} className='animate-spin' /> : <Search size={14} />}
                  >
                    Buscar
                  </Button>
                </div>
                <p className='mt-1.5 text-xs leading-relaxed text-[#6b778c]'>
                  Alternativa ao CPF. Buscar preenche só razão social e endereço.
                </p>
              </div>
              <div className='sm:col-span-2'>
                <Input
                  label='Razão social'
                  value={form.razaoSocial}
                  onChange={(e) => setForm({ ...form, razaoSocial: e.target.value })}
                />
              </div>
            </div>
          </PjCadastroFormSection>

          <PjCadastroFormSection
            icon={MapPin}
            title='Endereço'
            description='O CEP preenche logradouro, bairro, cidade e UF automaticamente.'
          >
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-6'>
              <div className='sm:col-span-2'>
                <Input
                  label='CEP *'
                  required
                  value={form.cep}
                  onChange={(e) => setForm({ ...form, cep: maskCep(e.target.value) })}
                  onBlur={() => void handleCepBlur()}
                  placeholder='00000-000'
                  inputMode='numeric'
                  maxLength={9}
                  hint={cepLoading ? 'Buscando endereço…' : undefined}
                />
              </div>
              <div className='sm:col-span-4'>
                <Input
                  label='Logradouro'
                  value={form.logradouro}
                  onChange={(e) => setForm({ ...form, logradouro: e.target.value })}
                />
              </div>
              <div className='sm:col-span-2'>
                <Input
                  label='Número *'
                  required
                  value={form.numero}
                  onChange={(e) => setForm({ ...form, numero: e.target.value })}
                />
              </div>
              <div className='sm:col-span-2'>
                <Input
                  label='Complemento'
                  value={form.complemento}
                  onChange={(e) => setForm({ ...form, complemento: e.target.value })}
                />
              </div>
              <div className='sm:col-span-2'>
                <Input
                  label='Bairro'
                  value={form.bairro}
                  onChange={(e) => setForm({ ...form, bairro: e.target.value })}
                />
              </div>
              <div className='sm:col-span-4'>
                <Input
                  label='Cidade'
                  value={form.cidade}
                  onChange={(e) => setForm({ ...form, cidade: e.target.value })}
                />
              </div>
              <div className='sm:col-span-2'>
                <Input
                  label='UF'
                  value={form.uf}
                  onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase().slice(0, 2) })}
                  placeholder='UF'
                  maxLength={2}
                  hint='Sigla do estado (2 letras).'
                />
              </div>
            </div>
          </PjCadastroFormSection>

          <PjCadastroFormSection
            icon={Landmark}
            title='Dados bancários *'
            description='Todos os campos desta seção são obrigatórios para pagamento.'
          >
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
              <Input
                label='Banco *'
                required
                value={form.bancoNome}
                onChange={(e) => setForm({ ...form, bancoNome: e.target.value })}
              />
              <PjCadastroSelect
                label='Tipo de conta *'
                required
                value={form.bancoTipoConta}
                onChange={(value) =>
                  setForm({ ...form, bancoTipoConta: value as PjAgradaFormData['bancoTipoConta'] })
                }
              >
                <option value=''>Selecione</option>
                <option value='corrente'>Corrente</option>
                <option value='poupanca'>Poupança</option>
              </PjCadastroSelect>
              <Input
                label='Agência *'
                required
                value={form.bancoAgencia}
                onChange={(e) => setForm({ ...form, bancoAgencia: e.target.value })}
              />
              <Input
                label='Conta *'
                required
                value={form.bancoConta}
                onChange={(e) => setForm({ ...form, bancoConta: e.target.value })}
              />
              <div className='sm:col-span-2'>
                <Input
                  label='Chave PIX *'
                  required
                  value={form.pixChave}
                  onChange={(e) => setForm({ ...form, pixChave: e.target.value })}
                />
              </div>
            </div>
          </PjCadastroFormSection>

          <PjCadastroCompletenessChecklist form={form} />

          <div className='flex flex-col gap-3 border-t border-[#e8edf5] pt-5 sm:flex-row sm:items-center sm:justify-between'>
            <p className='text-xs text-[#6b778c]'>
              Seus dados são usados apenas pelo financeiro Agrada e transmitidos de forma segura.
            </p>
            <Button type='submit' variant='primary' disabled={saving} className='w-full sm:w-auto'>
              {saving ? 'Enviando…' : 'Enviar cadastro'}
            </Button>
          </div>
        </form>
      </PjCadastroPanel>
    </PjCadastroPublicLayout>
  )
}
